import React from "react";

import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import { AssigneesPopover } from "../../../../../components/TVAssigneesPopover";

import { TVTodoEditAssigneesPopoverQuery } from "./__generated__/TVTodoEditAssigneesPopoverQuery.graphql";
import { TVTodoEditAssigneesPopoverMutation } from "./__generated__/TVTodoEditAssigneesPopoverMutation.graphql";
import { TVTodoEditAssigneesPopoverRolesMutation } from "./__generated__/TVTodoEditAssigneesPopoverRolesMutation.graphql";

type TodoEditAssigneesPopoverProps = {
  todoId: string;
  visible: boolean;
  onClose: () => void;
};

export function TVTodoEditAssigneesPopover(
  props: TodoEditAssigneesPopoverProps
) {
  const data = useLazyLoadQuery<TVTodoEditAssigneesPopoverQuery>(
    graphql`
      query TVTodoEditAssigneesPopoverQuery($id: ID!, $visible: Boolean!) {
        todo(id: $id) @include(if: $visible) {
          ...TVAssigneesPopover_assignable

          assigned {
            employee {
              id
            }
          }
          assignableRoles {
            ...TVAssigneesPopover_roles
          }
          assignedRoles {
            role {
              id
            }
          }
        }
      }
    `,
    {
      id: props.todoId,
      visible: props.visible,
    }
  );
  const [updateAssignees, isInFlight] =
    useMutation<TVTodoEditAssigneesPopoverMutation>(graphql`
      mutation TVTodoEditAssigneesPopoverMutation(
        $id: ID!
        $assignees: [ID!]!
        $employeeId: ID
      ) @raw_response_type {
        todo(todoId: $id, employeeId: $employeeId) {
          updateAssignedEmployees(updateAssignedEmployeeIds: $assignees) {
            id
            assigned {
              employee {
                id
              }
            }
          }
        }
      }
    `);

  const [updateAssignedRoles] =
    useMutation<TVTodoEditAssigneesPopoverRolesMutation>(graphql`
      mutation TVTodoEditAssigneesPopoverRolesMutation(
        $id: ID!
        $roleIds: [ID!]!
      ) @raw_response_type {
        todo(todoId: $id) {
          updateAssignedRoles(updateAssignedRoleIds: $roleIds) {
            id
            assignedRoles {
              role {
                id
              }
            }
          }
        }
      }
    `);

  if (!data.todo) {
    return null;
  }
  return (
    <AssigneesPopover
      key={props.visible ? "visible" : "hidden"}
      assignable={data.todo}
      visible={props.visible}
      onClose={props.onClose}
      roles={data.todo.assignableRoles}
      assignedRoleIds={data.todo.assignedRoles?.map((ar) => ar.role.id) ?? []}
      setAssignRoleIds={(selectedRoleIds) => {
        updateAssignedRoles({
          variables: {
            id: props.todoId,
            roleIds: selectedRoleIds,
          },
          optimisticResponse: {
            todo: {
              updateAssignedRoles: {
                id: props.todoId,
                assignedRoles: selectedRoleIds.map((id) => ({
                  id: id + "-optimistic",
                  role: { id },
                })),
              },
            },
          },
        });
      }}
      setAssignEmployeeIds={(selectedAssigneeIds) => {
        updateAssignees({
          variables: {
            id: props.todoId,
            assignees: selectedAssigneeIds,
          },
          optimisticResponse: {
            todo: {
              updateAssignedEmployees: {
                id: props.todoId,
                assigned: selectedAssigneeIds.map((id) => ({
                  id: id + "-optimistic",
                  employee: { id },
                })),
              },
            },
          },
        });
      }}
    />
  );
}
